import React, {
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	useEffect,
	useRef,
	useState,
	type ReactNode,
	type ComponentType,
} from 'react';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import { ErrorMessage, Field, HelperMessage } from '@atlaskit/form';
import type { StylesConfig } from '@atlaskit/select';
// @ts-expect-error - TS2614 - Module '"@atlaskit/user-picker"' has no exported member 'LegacyValue'. Did you mean to use 'import LegacyValue from "@atlaskit/user-picker"' instead?
import AkUserPicker, { type LegacyValue as AkUserPickerValue } from '@atlaskit/user-picker';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { toBaseUrl } from '@atlassian/jira-shared-types/src/general.tsx';
import messages from './messages';
import type { User } from './services/fetch-users/types';
import UseUsersDI from './services/use-users';
import type { UseUsers } from './types';
import { getOptions, getSelectedOptions } from './utils';

export const DEBOUNCE_TIMER = 300;

/**
 * Changing Props? You have to update the main README as well.
 */
export type PropsWithIntl = Omit<ComponentProps<typeof AkUserPicker>, 'value' | 'onChange'> & {
	defaultValue?: undefined; // value is mandatory, so defaultValue is not needed,
	loadOptions?: undefined; // use useUsers instead,
	isMulti?: boolean;
	value: string[];
	fieldId: string;
	specialOptions?: User[];
	addMoreMessage?: string;
	noOptionsMessage?: ((value: { inputValue: string }) => string | null) | null;
	noOptionsErrorMessages?: string;
	labelMessage?: string;
	placeholderMessage?: string;
	helpTextMessage?: string | ReactNode;
	errorMessage?: string;
	isError?: boolean;
	noPortal?: boolean; // set to true if rendering user picker inside an inline dialog,
	useUsers?: UseUsers;
	maxUsers?: number;
	onLoaded?: (isValid: boolean) => void;
	onError?: (e: Error) => void;
	onChange: (newValue: ReadonlyArray<User>) => void;
	intl: IntlShape;
	isClearable?: boolean;
	autoFocus?: boolean;
	isDisabled?: boolean;
	ariaDescribedBy?: string;
};

export type Props = Flow.Diff<
	PropsWithIntl,
	{
		intl: IntlShape;
	}
>;

const customStyles: StylesConfig = {
	menuPortal: (base) => ({ ...base, zIndex: layers.modal }),
};

export const UserPicker = (props: PropsWithIntl) => {
	const {
		value,
		onChange,
		fieldId,
		specialOptions = [],
		onLoaded = noop,
		onError = noop,
		noOptionsMessage = () => '',
		noOptionsErrorMessages,
		addMoreMessage = '',
		placeholderMessage = '',
		errorMessage = '',
		isError = false,
		labelMessage = '',
		useUsers = UseUsersDI,
		maxUsers,
		isMulti = true,
		helpTextMessage = '',
		intl: { formatMessage },
		noPortal = false,
		isClearable = true,
		ariaDescribedBy,
		...rest
	} = props;

	const baseUrl = toBaseUrl('');

	const [fetchData, { data, loading, error }] = useUsers(baseUrl, maxUsers);
	const valueRef = useRef(value);
	const portalEl = useRef(document.createElement('div'));
	const initialSearchFetch = useRef<unknown>([]);
	const [search, setSearch] = useState('');
	const prevSelectedOptions = useRef<unknown>([]);

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const body: HTMLElement | null = document.body as HTMLElement | null;
		const portalRef = portalEl.current;
		portalRef.setAttribute('id', PORTAL_ID);
		if (body && portalRef) {
			body.appendChild(portalRef);
		}
		return () => {
			if (body && portalRef) body.removeChild(portalRef);
		};
	}, []);

	useEffect(() => {
		fetchData({ accountIds: valueRef.current })
			.then(() => {
				onLoaded(Boolean(valueRef.current && valueRef.current.length));
			})
			.catch(onError);
	}, [fetchData, onLoaded, onError]);

	// @ts-expect-error - TS2571 - Object is of type 'unknown'.
	if (!initialSearchFetch.current.length && data.length) {
		initialSearchFetch.current = data;
	}

	const onUserSelect = (userPickerValue: AkUserPickerValue<User>) => {
		let newValue: ReadonlyArray<User> = [];
		if (userPickerValue) {
			newValue = Array.isArray(userPickerValue) ? userPickerValue : [userPickerValue];
		}
		onChange(newValue);
	};

	const onInputChange = debounce((query = '') => {
		setSearch(query);
		query && fetchData({ query }).catch(onError);
	}, DEBOUNCE_TIMER);

	const selectedOptions = getSelectedOptions({
		value,
		// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'User[]'.
		prevSelectedOptions: prevSelectedOptions.current,
		// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'User[]'.
		initialSearchData: initialSearchFetch.current,
		specialOptions,
		data,
	});

	prevSelectedOptions.current = selectedOptions;

	const options = getOptions({
		search,
		specialOptions,
		// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'User[]'.
		initialSearchData: initialSearchFetch.current,
		data,
		selectedOptions,
		isMulti,
	});

	const handleNoOptionMessage = () => {
		const noOptionsMsg = !isEmpty(noOptionsMessage)
			? noOptionsMessage
			: formatMessage(messages.noUsersFound);

		return error && noOptionsErrorMessages ? noOptionsErrorMessages : noOptionsMsg;
	};

	const showErrorMessage = !noOptionsErrorMessages && (error || isError);

	return (
		<Field
			name="jira-user-picker"
			{...(!isEmpty(labelMessage) && { label: labelMessage })}
			testId="user-picker-next.field"
			id={fieldId}
		>
			{({ fieldProps: { id, ...remainingFieldProps } }) => (
				<>
					<AkUserPicker
						{...remainingFieldProps}
						{...rest}
						width="100%"
						appearance="normal"
						isClearable={isClearable}
						fieldId={id}
						menuPortalTarget={noPortal === true ? undefined : portalEl.current}
						isMulti={isMulti}
						isLoading={loading}
						options={options}
						onChange={onUserSelect}
						onInputChange={onInputChange}
						value={selectedOptions}
						placeholder={
							!isEmpty(placeholderMessage)
								? placeholderMessage
								: formatMessage(messages.placeholder)
						}
						// @ts-expect-error - TS2322 - Type is not assignable string | null
						noOptionsMessage={() => handleNoOptionMessage()}
						addMoreMessage={
							!isEmpty(addMoreMessage) ? addMoreMessage : formatMessage(messages.addMoreMessage)
						}
						styles={customStyles}
						aria-describedby={ariaDescribedBy}
					/>

					{showErrorMessage && (
						<ErrorMessage>
							{!isEmpty(errorMessage) ? errorMessage : formatMessage(messages.error)}
						</ErrorMessage>
					)}

					{!showErrorMessage && !isEmpty(helpTextMessage) && (
						<HelperMessage>{helpTextMessage}</HelperMessage>
					)}
				</>
			)}
		</Field>
	);
};

const UserPickerWithIntl: ComponentType<Props> = injectIntl(UserPicker);

export default UserPickerWithIntl;

const PORTAL_ID = 'platform-ui-user-picker-menu-portal-target-wrapper';
